import React, { useState, useEffect, useRef } from 'react';

interface CarouselProps {
  images: string[] | { src: string; caption?: string }[];
}

const Carousel: React.FC<CarouselProps> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  // Determine if we're dealing with simple strings or image objects
  const isStringArray = typeof images[0] === 'string';
  
  // Get image sources array for calculations
  const imageSources = isStringArray 
    ? images as string[] 
    : (images as { src: string; caption?: string }[]).map(img => img.src);
    
  const totalImages = imageSources.length;

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalImages);
    
    // Reset transition state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalImages) % totalImages);
    
    // Reset transition state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    
    // Reset transition state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [currentIndex]);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
    }
  }, [currentIndex]);
  
  // Preload adjacent images whenever the current slide changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      return; // Skip on server-side rendering
    }
    
    // Define indexes of images to preload
    const prevIndex = (currentIndex - 1 + totalImages) % totalImages;
    const nextIndex = (currentIndex + 1) % totalImages;
    
    // Create image objects to trigger browser preloading
    const preloadImages = [currentIndex, prevIndex, nextIndex].map(index => {
      const img = new Image();
      img.src = imageSources[index];
      return img;
    });
    
    // Cleanup function to allow garbage collection
    return () => {
      preloadImages.forEach(img => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [currentIndex, imageSources, totalImages]);

  // Touch events for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 75) { // Swiped left
      handleNext();
    } else if (touchStartX.current - touchEndX.current < -75) { // Swiped right
      handlePrev();
    }
    // Reset values
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  return (
    <div className="carousel-container">
      <div 
        className="carousel-wrapper" 
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {imageSources.map((src, index) => {
          // Determine if this image should be loaded
          // Only load current, prev, and next images for better performance
          const shouldLoad = 
            index === currentIndex || 
            index === (currentIndex - 1 + totalImages) % totalImages || 
            index === (currentIndex + 1) % totalImages;
            
          return (
            <div className="carousel-slide" key={index}>
              {shouldLoad ? (
                <>
                  <img src={src} alt={`Slide ${index + 1}`} loading="lazy" />
                  {!isStringArray && (images as { src: string; caption?: string }[])[index].caption && (
                    <div className="image-caption">{(images as { src: string; caption?: string }[])[index].caption}</div>
                  )}
                </>
              ) : (
                <div className="carousel-placeholder" style={{ width: '100%', height: '100%', background: '#f5f5f5' }}></div>
              )}
            </div>
          );
        })}
      </div>

      {totalImages > 1 && (
        <>
          <button className="carousel-button prev" onClick={handlePrev}>&#10094;</button>
          <button className="carousel-button next" onClick={handleNext}>&#10095;</button>
          
          <div className="carousel-dots">
            {imageSources.map((_, index) => (
              <span
                key={index}
                className={`carousel-dot ${currentIndex === index ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              ></span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Carousel; 